import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

/**
 * Mantine theme built from playbox-flutter/docs/design/design-system/tokens.json.
 * The CMS uses the grown-up palette at CMS density: Poppins only, `target-cms` (36px) controls,
 * `radius-s` corners, no doodles or display type.
 */

// Ten-step ramps around the brand's -100 / -300 / -700 values (index 1, 4, 8).
const sunshine: MantineColorsTuple = [
  '#fffbe8',
  '#ffefa1',
  '#ffe885',
  '#ffdd6e',
  '#ffd45e',
  '#f7c43c',
  '#e0ab1c',
  '#b38a0f',
  '#765900',
  '#4d3a00',
];
const sky: MantineColorsTuple = [
  '#eef7fc',
  '#d9edf8',
  '#c3e2f2',
  '#b4d8ec',
  '#a7cfe6',
  '#79b2d3',
  '#4f92b8',
  '#3a80a6',
  '#2a6e94',
  '#1d4f6b',
];
const leaf: MantineColorsTuple = [
  '#eefaea',
  '#c0edad',
  '#acdf97',
  '#9dd686',
  '#92cf79',
  '#74b85a',
  '#5a9a41',
  '#4a8433',
  '#3c6e27',
  '#2a4e1b',
];
const coral: MantineColorsTuple = [
  '#fff0f0',
  '#ffaead',
  '#ffa09f',
  '#ff9594',
  '#ff8b8a',
  '#ea6764',
  '#d2504c',
  '#c24440',
  '#b23a36',
  '#7f2926',
];
const orange: MantineColorsTuple = [
  '#fff5ea',
  '#ffd7a6',
  '#ffc883',
  '#ffbc69',
  '#ffb152',
  '#ec9830',
  '#cf7e1c',
  '#b06812',
  '#93520a',
  '#663905',
];
const lilac: MantineColorsTuple = [
  '#f4f3fb',
  '#dedaf3',
  '#cfcae6',
  '#c3bedc',
  '#b9b4d4',
  '#9d96c2',
  '#8279b0',
  '#7066a3',
  '#5f5596',
  '#433c6b',
];
const blossom: MantineColorsTuple = [
  '#fff0f8',
  '#ffc2e8',
  '#f9bbd6',
  '#f5b7cd',
  '#f2b3c4',
  '#e48aa3',
  '#cf6283',
  '#bc4a6e',
  '#a8335a',
  '#76233f',
];
// Warm greys from the paper/ink tokens (light theme neutrals).
const ink: MantineColorsTuple = [
  '#fffaf3',
  '#f6eee2',
  '#ede3d4',
  '#e6dccd',
  '#cfc5b8',
  '#a59d95',
  '#857e78',
  '#6b6560',
  '#434242',
  '#2a2929',
];
// Night theme (Mantine reads `dark` for the dark colour scheme).
const dark: MantineColorsTuple = [
  '#fbf4ea',
  '#e3dbe6',
  '#c8c0d3',
  '#9a95b0',
  '#3a3c54',
  '#313349',
  '#262838',
  '#1c1d2b',
  '#161722',
  '#0b0c14',
];

export const theme = createTheme({
  fontFamily: 'Poppins, system-ui, sans-serif',
  headings: {
    fontFamily: 'Poppins, system-ui, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '1.5rem', lineHeight: '1.3' },
      h2: { fontSize: '1.25rem', lineHeight: '1.35' },
      h3: { fontSize: '1.0625rem', lineHeight: '1.4' },
      h4: { fontSize: '0.9375rem', lineHeight: '1.4' },
    },
  },
  black: '#434242',
  white: '#ffffff',
  colors: {
    sunshine,
    sky,
    leaf,
    coral,
    orange,
    lilac,
    blossom,
    ink,
    dark,
    gray: ink,
    red: coral,
    green: leaf,
    blue: sky,
  },
  primaryColor: 'sunshine',
  primaryShade: { light: 4, dark: 4 },
  autoContrast: true,
  luminanceThreshold: 0.45,
  defaultRadius: 'sm',
  radius: { xs: '4px', sm: '8px', md: '14px', lg: '24px', xl: '32px' },
  focusRing: 'auto',
  cursorType: 'pointer',
  components: {
    Button: { defaultProps: { size: 'sm' } },
    TextInput: { defaultProps: { size: 'sm' } },
    Table: { defaultProps: { verticalSpacing: 'xs', fz: 'sm', highlightOnHover: true } },
    Badge: { defaultProps: { radius: 'sm', variant: 'light' } },
    Tooltip: { defaultProps: { withArrow: true, openDelay: 200 } },
    Modal: { defaultProps: { radius: 'md' } },
    Anchor: { defaultProps: { c: 'sky.8' } },
  },
});

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': '#fffaf3',
    '--mantine-color-text': '#434242',
    '--mantine-color-dimmed': '#6b6560',
    '--mantine-color-default-border': '#d9cdbb',
    '--pb-surface': '#ffffff',
    '--pb-surface-sunken': '#f6eee2',
    '--pb-hairline': '#e6dccd',
    '--pb-outline': '#434242',
    '--pb-focus': '#2a6e94',
    '--pb-danger': '#b23a36',
    '--pb-success': '#3c6e27',
    '--pb-warning': '#93520a',
    '--pb-info': '#2a6e94',
    '--mantine-color-anchor': '#2a6e94',
  },
  dark: {
    '--mantine-color-body': '#1c1d2b',
    '--mantine-color-text': '#fbf4ea',
    '--mantine-color-dimmed': '#c8c0d3',
    '--pb-surface': '#262838',
    '--pb-surface-sunken': '#161722',
    '--pb-hairline': '#3a3c54',
    '--pb-outline': '#9a95b0',
    '--pb-focus': '#a7cfe6',
    '--pb-danger': '#ff8b8a',
    '--pb-success': '#92cf79',
    '--pb-warning': '#ffb152',
    '--pb-info': '#a7cfe6',
    '--mantine-color-anchor': '#a7cfe6',
  },
});
